import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import {
  Grid,
  List,
  ListItem,
  Divider,
  Chip,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import formatDate from "../../../utilities/formatDate";
import request from "../../../utilities/requests";

const LeagueEvents = () => {
  const navigate = useNavigate();
  const [leagueEvents, setLeagueEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("upcoming");

  useEffect(() => {
    if (!loaded) {
      getLeagueEvents();
      setLoaded(true);
    }
  });

  useEffect(() => {
    applyFilters(leagueEvents, statusFilter);
  }, [leagueEvents, statusFilter]);

  const getLeagueEvents = async () => {
    const result = await request("leagueevent");
    if (result?.success) {
      setLeagueEvents(result.data);
    }
  };

  const applyFilters = (events, status) => {
    let result = [...events];
    const now = moment();
    if (status === "upcoming") {
      result = result.filter((le) => {
        const date = moment(le?.event?.date);
        return date.isSameOrAfter(now, "day");
      });
    } else if (status === "past") {
      result = result.filter((le) => {
        const date = moment(le?.event?.date);
        return date.isBefore(now, "day");
      });
    }
    setFilteredEvents(result);
  };

  const getListItemColor = (leagueEvent) => {
    try {
      const userTimezone = moment.tz.guess();
      const now = moment().tz(userTimezone);
      const date = moment(leagueEvent?.event?.date).tz(userTimezone);
      return now.isSame(date, "day") ? "rgb(222 221 221)" : null;
    } catch (e) {
      return null;
    }
  };

  const handleClick = (leagueEvent) => {
    navigate(`/leaguecontroller/${leagueEvent?.event?._id}`);
  };

  return (
    <Grid container flexDirection="column" sx={{ width: "100%" }}>
      <Grid item sx={{ mb: 2 }}>
        <FormControl variant="outlined" sx={{ width: { xs: "100%", sm: 220 } }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ height: 40 }}
          >
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="past">Past</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item>
        <List>
          {filteredEvents.map((le) => (
            <div key={le._id} style={{ padding: 0 }}>
              <ListItem
                style={{
                  backgroundColor: getListItemColor(le),
                  padding: 10,
                  cursor: "pointer",
                }}
                onClick={() => handleClick(le)}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10 }}>
                    <Typography variant="body2">
                      {le?.event?.name}
                    </Typography>
                    {le?.sportType && (
                      <Chip label={le.sportType} size="small" variant="outlined" />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      {formatDate(le?.event?.date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
                    <Typography variant="caption" color="text.secondary">
                      {le?.teams?.length || 0} team{le?.teams?.length !== 1 ? "s" : ""}
                    </Typography>
                    <Chip
                      label={le?.standingsMode === "realtime" ? "Live Standings" : "Manual Standings"}
                      size="small"
                      color={le?.standingsMode === "realtime" ? "success" : "default"}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </ListItem>
              <Divider />
            </div>
          ))}
          {filteredEvents.length === 0 && (
            <ListItem>
              <Typography variant="body2" color="text.secondary">
                No league events found.
              </Typography>
            </ListItem>
          )}
        </List>
      </Grid>
    </Grid>
  );
};

export default LeagueEvents;
